// Eskom PARCC — pure helpers for the Allocation board's reviewer + review-status
// filtering and the validated reviewer dropdown (owner feedback point 2).
//
// React-free, Firebase-free so they unit-test in isolation. The board uses the
// shared 9-facet model for the standard filters; these add the two
// allocation-specific dimensions: filter by reviewer (validated against the
// active user list, plus an "Unassigned" sentinel) and by the four-state review
// status (blank counts as "Not Reviewed").
import { normalizeEmail } from '../../lib/email';
import { REVIEW_STATUS_OPTIONS } from '../../domain/enums';
import type { Transaction } from '../../data/types';

/** Sentinel value for the "Unassigned" choice in the reviewer filter. */
export const UNASSIGNED = '__unassigned__';

const DEFAULT_STATUS: (typeof REVIEW_STATUS_OPTIONS)[number] = 'Not Reviewed';

/** The minimal grant shape the reviewer dropdown needs (from useAccessGrants). */
export interface ReviewerGrantLike {
  email: string;
  displayName: string | null;
  active: boolean;
}

export interface ReviewerOption {
  value: string;
  label: string;
}

/**
 * The selectable reviewers for assignment/filtering: active grants only, keyed
 * by normalised email, labelled by display name (falling back to the email),
 * de-duplicated and sorted by label.
 */
export function reviewerOptions(grants: readonly ReviewerGrantLike[]): ReviewerOption[] {
  const seen = new Set<string>();
  const opts: ReviewerOption[] = [];
  for (const g of grants) {
    if (!g.active) continue;
    const email = normalizeEmail(g.email);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    opts.push({ value: email, label: (g.displayName ?? '').trim() || email });
  }
  return opts.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

/** The review status a transaction is shown/filtered under — blank ⇒ Not Reviewed. */
export function effectiveReviewStatus(tx: Transaction): string {
  return String(tx.reviewStatus ?? '').trim() || DEFAULT_STATUS;
}

/** True when the transaction matches the selected reviewer (or no reviewer is selected). */
export function matchesReviewer(tx: Transaction, selected: string): boolean {
  if (!selected) return true;
  const assignee = normalizeEmail(tx.assignedToEmail);
  if (selected === UNASSIGNED) return assignee === '';
  return assignee === normalizeEmail(selected);
}

/** True when the transaction matches the selected review status (or none is selected). */
export function matchesReviewStatus(tx: Transaction, selected: string): boolean {
  if (!selected) return true;
  return effectiveReviewStatus(tx) === selected;
}
